import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Site-wide scroll reveal.
 *
 * Content lifts gently into place the first time it scrolls into view. Once
 * revealed it stays revealed.
 *
 * The design point worth understanding:
 *
 *   An element is only ever hidden if it is BELOW THE FOLD at the moment we
 *   first see it. Anything already on screen is marked revealed immediately
 *   and never gets the hidden state at all.
 *
 * That single rule removes three problems at once:
 *
 *   1. No flash. Content in view is never hidden and then animated back in.
 *   2. No race with the network. Products arriving late are measured when they
 *      arrive, not against a timer that has already expired.
 *   3. No blank page. The hidden state only exists on elements this code has
 *      touched, so if the bundle fails to load or this file throws, nothing is
 *      hidden — there is no global "hide everything" class to get stuck on.
 *
 * The earlier version added a `js-reveal` class to <html> up front and used a
 * 1.2s timer to undo it if the observer never fired. On a slow page the timer
 * won the race and killed the reveal on exactly the pages that needed it.
 *
 * Everything animates transform and opacity only — no layout, no paint.
 */

// Existing class names across the site, so no page markup has to change.
const SELECTOR = [
  ".home-product-card",
  ".home-shelf",
  ".home-section-label",
  ".product-card-container",
  ".pd-block",
  ".pd-reviews",
  ".about-values .about-value-item",
  ".policy-section",
  ".order-history-card",
  ".size-guide-section",
  "[data-reveal]",
].join(",");

const HIDDEN = "reveal";
const SHOWN = "is-in";
const SEEN = "data-reveal-seen";

export default function useScrollReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;

    // Reduced motion: nothing hidden, nothing observed, no work done.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(SHOWN);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
    );

    let below = 0; // index used for stagger, per batch of off-screen elements

    const collect = () => {
      const viewportH = window.innerHeight || document.documentElement.clientHeight;

      document.querySelectorAll(SELECTOR).forEach((el) => {
        if (el.hasAttribute(SEEN)) return;
        el.setAttribute(SEEN, "");

        const top = el.getBoundingClientRect().top;

        // Already on screen, or scrolled past. Never hide it.
        if (top < viewportH * 0.92) {
          el.classList.add(HIDDEN, SHOWN);
          return;
        }

        // Below the fold: safe to hide, then reveal on approach.
        el.classList.add(HIDDEN);
        el.style.setProperty("--reveal-delay", `${Math.min(below % 8, 7) * 50}ms`);
        below += 1;
        observer.observe(el);
      });
    };

    // Belt and braces. This does on scroll exactly what the observer does, so
    // IntersectionObserver is an optimisation rather than a dependency. In a
    // throttled or backgrounded tab the observer can simply never deliver a
    // callback; without this, everything below the fold would stay hidden no
    // matter how far the visitor scrolled.
    const sweep = () => {
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      document.querySelectorAll("." + HIDDEN + ":not(." + SHOWN + ")").forEach((el) => {
        if (el.getBoundingClientRect().top < viewportH * 0.92) {
          el.classList.add(SHOWN);
          observer.unobserve(el);
        }
      });
    };

    const onScroll = () => {
      collect();
      sweep();
    };

    collect();

    // Products and orders are fetched after mount, so watch for them arriving.
    const mo = new MutationObserver(onScroll);
    mo.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      mo.disconnect();
      observer.disconnect();
    };
  }, [pathname]);
}
